import { Injectable, OnDestroy } from '@angular/core';
import { PubSubService, PubSubRegistration, declareEvent } from './pubsub.service';
import { FocusStateService, FocusState, focusStateChangedEvent } from './focus-state.service';
import { DatabaseState, CloudStateChangeEvent } from './database-wrapper';
import { ConnectionState, connectionStateChangedEvent, OnlineStateService } from './online-state.service';
import { TelemetryService } from './telemetry.service';
import { SPLIT_BRAIN_LATCH_KEY, shouldProbeForSplitBrain } from './connectivity-split-brain';
import {
  decideOfflineDebounceAction,
  OfflineDebounceAction,
  shouldExtendOfflineDebounceOnReconnect,
} from './offline-debounce-gate';
import { createTimeoutSignal } from './timeout-signal';
import { AppEnvironment } from 'environments/environment';
import { LoggingService } from './logging.service';

/** How long we give the CouchDB split-brain probe before calling it unreachable. */
const SPLIT_BRAIN_PROBE_TIMEOUT_MS = 5000;

/** How long sync is given to come back before we tell the user we are offline. Named rather than
 *  written inline because the reconnect extension below restarts this same window, so how long the
 *  worst case can get is worked out from it (df-c29bf5fc50cd). */
const OFFLINE_DEBOUNCE_MS = 5000;

/** Is the page actually on screen? (#3991)
 *
 * Deliberately NOT "is the window focused". A visible-but-unfocused window is still running and
 * must still tell the truth about connectivity; only a genuinely hidden/backgrounded page — whose
 * networking the OS may have suspended — should hold back. */
function isPageVisible(): boolean {
  return document.visibilityState === 'visible';
}

// Event published when debounced state changes
export const DebouncedCloudStateChangeEvent = declareEvent<{ state: DatabaseState }>('debouncedCloudStateChanged');

@Injectable({
  providedIn: 'root'
})
export class ConnectionStateService implements OnDestroy {
  private rawCloudState: DatabaseState = undefined;
  private _debouncedState: DatabaseState = undefined;
  private offlineDebounceTimer: ReturnType<typeof setTimeout>;
  /** df-c29bf5fc50cd: has the pending offline countdown already had its one reconnect extension?
   *  Cleared together with the countdown itself, because that is the moment the pending transition
   *  ends and the next one starts with its extension unspent. */
  private offlineDebounceExtended = false;

  private cloudStateSub: PubSubRegistration;
  private focusStateSub: PubSubRegistration;
  private connectionStateSub: PubSubRegistration;
  /** #3991: our own `visibilitychange` listener — see the constructor for why the focus event alone
   *  is not enough. Kept so we can remove it in ngOnDestroy. */
  private readonly onVisibilityChange = () => this.reevaluateOfflineTransition();

  constructor(
    private pubSub: PubSubService,
    // Injected purely to force construction: FocusStateService is what registers the focus/visibility
    // listeners behind focusStateChangedEvent, which we subscribe to below. We never read its
    // focusState any more (we ask the document for visibility instead — see isPageVisible), so the
    // parameter is deliberately unused; the leading underscore is what tells TypeScript that is
    // intentional rather than an oversight.
    _focusStateService: FocusStateService,
    private onlineStateService: OnlineStateService,
    private telemetry: TelemetryService,
    private logging: LoggingService
  ) {
    this.cloudStateSub = this.pubSub.subscribe(CloudStateChangeEvent, (data: { state: DatabaseState }) => {
      this.onCloudStateChanged(data.state);
    });

    this.focusStateSub = this.pubSub.subscribe(focusStateChangedEvent, (_state: FocusState) => {
      this.reevaluateOfflineTransition();
    });

    // df-c29bf5fc50cd: the countdown above is waiting for sync to come back, and this is the event
    // that says the network it needs is available again. Without this subscription the countdown
    // races the very recovery it is waiting for — see onConnectionStateChanged.
    this.connectionStateSub = this.pubSub.subscribe(connectionStateChangedEvent, (state: ConnectionState) => {
      this.onConnectionStateChanged(state === ConnectionState.Online);
    });

    // #3991: listen to `visibilitychange` DIRECTLY, as well as the focus event.
    //
    // We DECIDE on document.visibilityState, so we must also SUBSCRIBE to it. focusStateChangedEvent
    // is not a substitute: FocusStateService collapses focus, blur and visibilitychange into one
    // boolean and only publishes when that boolean FLIPS. So a window that is already blurred but
    // still visible (FocusState is Off) can be hidden, and no event is published at all — leaving a
    // pending offline transition scheduled, which would then commit while the page is hidden. That
    // is the exact case the visibility gate exists to prevent, so the gate must not depend on a
    // signal that can silently fail to arrive.
    document.addEventListener('visibilitychange', this.onVisibilityChange);
  }

  ngOnDestroy() {
    this.clearOfflineDebounceTimer();
    this.cloudStateSub?.unsubscribe();
    this.focusStateSub?.unsubscribe();
    this.connectionStateSub?.unsubscribe();
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
  }

  get debouncedState(): DatabaseState {
    return this._debouncedState;
  }

  private setDebouncedState(state: DatabaseState) {
    if (this._debouncedState !== state) {
      this._debouncedState = state;
      this.pubSub.publish(DebouncedCloudStateChangeEvent, { state });
    }
  }

  private clearOfflineDebounceTimer() {
    if (this.offlineDebounceTimer !== undefined) {
      clearTimeout(this.offlineDebounceTimer);
      this.offlineDebounceTimer = undefined;
    }
    // clearing the countdown ends the pending transition, so the next one starts with its one
    // reconnect extension unspent (df-c29bf5fc50cd)
    this.offlineDebounceExtended = false;
  }

  /** Re-decide what to do with a pending offline transition. Called on BOTH signals — the focus
   *  event and our own visibilitychange listener — because the focus event alone can silently fail
   *  to arrive (see the constructor).
   *
   *  #3991: this used to key on FOCUS, which was wrong. FocusStateService reports Off on the
   *  window's `blur` event, and a window can be blurred while still perfectly VISIBLE — the app
   *  sitting in a side-by-side window while the user works in another one, or simply having
   *  DevTools focused. In that state the offline debounce was never scheduled, so the app went on
   *  showing "online" while it plainly was not. Visibility is the signal we actually want.
   *
   *  The decision itself is the pure `decideOfflineDebounceAction`, which has a table test; it has
   *  been wrong twice, in opposite directions. */
  private reevaluateOfflineTransition() {
    const action = decideOfflineDebounceAction({
      pageVisible: isPageVisible(),
      rawStateIsOffline: this.rawCloudState === DatabaseState.Offline,
      debouncedStateIsOffline: this._debouncedState === DatabaseState.Offline,
      timerAlreadyRunning: this.offlineDebounceTimer !== undefined,
    });

    if (action === OfflineDebounceAction.Cancel) {
      this.clearOfflineDebounceTimer();
    } else if (action === OfflineDebounceAction.Schedule) {
      this.scheduleOfflineTransition();
    }
  }

  private onCloudStateChanged(newState: DatabaseState) {
    this.rawCloudState = newState;

    // clear timer on any cloud state change
    this.clearOfflineDebounceTimer();

    // if transitioning to offline, start debounce timer
    if (newState === DatabaseState.Offline) {
      this.scheduleOfflineTransition();
    } else {
      // for non-offline states, update immediately
      this.setDebouncedState(newState);
    }
  }

  private scheduleOfflineTransition() {
    this.clearOfflineDebounceTimer();

    // Only while the page is VISIBLE — not merely while it is focused (#3991).
    //
    // The suppression exists because a backgrounded app (iOS especially) has its networking
    // suspended, so it would claim to be offline when it simply is not running. A hidden page is
    // exactly that case, and it is still covered here. But an app that is visible and merely
    // unfocused IS running, and if it cannot reach the server the user deserves to be told.
    if (!isPageVisible()) {
      return;
    }

    this.startOfflineDebounceCountdown();
  }

  /**
   * df-c29bf5fc50cd: the connection state has changed. The only thing we do with that here is give
   * a countdown that is racing a recovery one more `OFFLINE_DEBOUNCE_MS` to finish it.
   *
   * `shouldExtendOfflineDebounceOnReconnect` carries the whole reasoning: why this event is
   * evidence a recovery is under way, why the countdown is restarted rather than cancelled, and
   * what stops it ever failing to land.
   */
  private onConnectionStateChanged(connectionOnline: boolean) {
    const shouldExtend = shouldExtendOfflineDebounceOnReconnect({
      connectionNowOnline: connectionOnline,
      countdownRunning: this.offlineDebounceTimer !== undefined,
      extensionAlreadySpent: this.offlineDebounceExtended,
    });
    if (!shouldExtend) return;

    console.log('ConnectionState: network confirmed back — giving sync one more debounce window before showing Offline');
    // clearTimeout directly rather than clearOfflineDebounceTimer(): that helper also resets the
    // extension flag, because it means "this pending transition is over". This transition is not
    // over — it is the same one, restarted — so the flag must survive and be marked as spent.
    clearTimeout(this.offlineDebounceTimer);
    this.offlineDebounceExtended = true;
    this.startOfflineDebounceCountdown();
  }

  /** Start the countdown itself. Split out from `scheduleOfflineTransition` so the reconnect
   *  extension above can restart it without going through that method's clear-and-reset, which
   *  would wipe the record of the extension it has just granted. */
  private startOfflineDebounceCountdown() {
    this.offlineDebounceTimer = setTimeout(() => {
      this.setDebouncedState(DatabaseState.Offline);
      // the transition has committed, so it is over: drop the handle AND the record of whether it
      // used its reconnect extension, so a later transition starts with a clean slate
      this.clearOfflineDebounceTimer();
      // F10: sync has now definitively given up. If the origin probe still thinks we are online,
      // the two halves of the app disagree — go and measure it. Fire-and-forget: this is pure
      // measurement and must never affect the state machine or delay it.
      //
      // The catch is not decoration: this runs from a setTimeout callback, so a throw inside it
      // (sessionStorage can throw in Safari private mode or when the quota is full; trackEvent can
      // throw) would surface as an unhandled rejection with no owner. Measurement failing must never
      // be louder than the thing it measures — but it must not be silent either.
      void this.measureSplitBrainIfNeededAsync().catch(err => {
        console.log('ConnectionState: split-brain measurement failed', err);
        this.logging.trackSilentProblem('ConnectionState: split-brain measurement threw', {
          error: err?.message ?? String(err),
        });
      });
    }, OFFLINE_DEBOUNCE_MS);
  }

  /** F10 (#3991): measure the origin/CouchDB split. Sync has just gone Offline; if the origin
   *  probe still says Online, ping CouchDB itself and report which of the two was right.
   *
   *  MEASUREMENT ONLY — nothing here changes the connection state or the debounced state. The
   *  point is to find out how often a reachable origin hides an unreachable CouchDB, so a
   *  follow-up can decide whether the reachability probe should ping CouchDB instead. */
  private async measureSplitBrainIfNeededAsync(): Promise<void> {
    const shouldProbe = shouldProbeForSplitBrain({
      connectionOnline: this.onlineStateService.connectionState === ConnectionState.Online,
      alreadyReportedThisSession: sessionStorage.getItem(SPLIT_BRAIN_LATCH_KEY) === 'true',
    });
    if (!shouldProbe) return;

    // latch BEFORE the await, not after: two debounced Offline commits could otherwise both get
    // past the check while the first probe is still in flight, and we would report twice.
    sessionStorage.setItem(SPLIT_BRAIN_LATCH_KEY, 'true');

    const { reachable, failure } = await this.probeCouchReachableAsync();
    this.telemetry.trackEvent('connectivity_split_brain', {
      // true by definition — this only runs when the origin probe says we are online
      origin_reachable: true,
      couch_reachable: reachable,
      // WHY it was unreachable. Without this the measurement is nearly useless: a timeout, a DNS
      // failure and a CORS rejection would all read as "CouchDB is down", and this event exists
      // precisely to decide whether to re-target the probe. Undefined when CouchDB answered.
      couch_failure: failure,
    });
  }

  /** One plain fetch at CouchDB's own health endpoint. Any answer at all — even a non-2xx —
   *  means CouchDB is reachable; only a thrown error (including the 5 s timeout) means it is not.
   *  Returns WHY it failed, not just that it did — see the telemetry call above. */
  private async probeCouchReachableAsync(): Promise<{ reachable: boolean; failure?: string }> {
    try {
      await fetch(`${AppEnvironment.pouchConfig.couch_serverurl}_up`, {
        method: 'HEAD',
        cache: 'no-cache',
        signal: createTimeoutSignal(SPLIT_BRAIN_PROBE_TIMEOUT_MS),
      });
      return { reachable: true };
    } catch (err) {
      // an AbortError here is our own 5 s timeout; a TypeError is usually DNS/CORS/connection-refused
      return { reachable: false, failure: err?.name ? `${err.name}: ${err.message ?? ''}`.trim() : String(err) };
    }
  }
}
